/** CRM-5 Dashboard Executive — KPI + funnel + breakdown */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Activity, TrendingUp, Users, FileText, Euro, RefreshCw, MessageCircle, ListChecks, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtPct = (n) => `${(((n || 0) * 100)).toFixed(1)}%`;

export default function CRMDashboardPage() {
  const today = new Date().toISOString().slice(0, 10);
  const lastMonth = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(lastMonth);
  const [to, setTo] = useState(today);
  const [kpi, setKpi] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [ops, setOps] = useState([]);
  const [compagnie, setCompagnie] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, f, o, c] = await Promise.all([
        axios.get(`${API}/api/admin/crm/dashboard/kpi?date_from=${from}&date_to=${to}`, { withCredentials: true }),
        axios.get(`${API}/api/admin/crm/dashboard/funnel-lead`, { withCredentials: true }),
        axios.get(`${API}/api/admin/crm/dashboard/by-operatore`, { withCredentials: true }),
        axios.get(`${API}/api/admin/crm/dashboard/by-compagnia`, { withCredentials: true }),
      ]);
      setKpi(k.data); setFunnel(f.data); setOps(o.data.items || []); setCompagnie(c.data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!kpi) return <p className="text-zinc-400 italic text-center py-6">Dashboard non disponibile</p>;

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="crm-dashboard-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><Activity className="w-5 h-5" /> Dashboard Executive</h2>
          <p className="text-xs text-zinc-500">Cache 5min · refresh manuale per dati live</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-2 py-1 rounded border border-zinc-200" data-testid="dash-date-from" />
          <span className="text-zinc-400">→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-2 py-1 rounded border border-zinc-200" data-testid="dash-date-to" />
          <button onClick={load} className="p-1.5 rounded bg-zinc-100 hover:bg-zinc-200"><RefreshCw className="w-3 h-3" /></button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dash-kpi-grid">
        <KpiCard icon={Users} title="Lead totali" value={kpi.lead.totale} sub={`Conv. ${fmtPct(kpi.lead.conversion_rate)}`} testid="kpi-lead-totale" />
        <KpiCard icon={TrendingUp} title="Lead nuovi" value={kpi.lead.nuovi} sub={`${kpi.lead.convertiti} convertiti`} color="emerald" testid="kpi-lead-nuovi" />
        <KpiCard icon={Users} title="Clienti" value={kpi.clienti.totale} sub={`+${kpi.clienti.nuovi_periodo} nel periodo`} testid="kpi-clienti" />
        <KpiCard icon={FileText} title="Polizze attive" value={kpi.polizze.totale_attive} sub={`${kpi.polizze.nuove_emesse_periodo} nuove`} testid="kpi-polizze" />
        <KpiCard icon={Euro} title="Premio totale" value={fmtEur(kpi.polizze.premi_totali_eur)} sub="Polizze attive" color="amber" testid="kpi-premio" />
        <KpiCard icon={Euro} title="Incassi periodo" value={fmtEur(kpi.pagamenti.incassati_periodo_eur)} sub={`${fmtEur(kpi.pagamenti.in_attesa_eur)} in attesa`} color="emerald" testid="kpi-incassi" />
        <KpiCard icon={RefreshCw} title="Rinnovi pipeline" value={kpi.rinnovi.in_pipeline} sub={`${fmtPct(kpi.rinnovi.conversion_rate)} conv.`} testid="kpi-rinnovi" />
        <KpiCard icon={MessageCircle} title="WhatsApp inviati" value={kpi.comunicazioni.whatsapp_inviati_periodo} sub={`Delivery ${fmtPct(kpi.comunicazioni.delivery_rate)}`} testid="kpi-whatsapp" />
        <KpiCard icon={ListChecks} title="Task aperti" value={kpi.task.aperti} sub={kpi.task.scaduti > 0 ? `${kpi.task.scaduti} scaduti!` : "OK"} color={kpi.task.scaduti > 0 ? "rose" : "zinc"} testid="kpi-task" />
      </div>

      {/* Funnel Lead */}
      {funnel && (
        <div className="border border-zinc-200 rounded-xl p-4" data-testid="dash-funnel">
          <h3 className="font-semibold mb-3">Funnel Lead</h3>
          <div className="space-y-1">
            {funnel.stages.map((s, i) => {
              const max = Math.max(...funnel.stages.map(x => x.count), 1);
              const w = (s.count / max) * 100;
              const drop = i > 0 ? funnel.drop_off_rates[i - 1] : null;
              return (
                <div key={s.name} className="flex items-center gap-2 text-xs">
                  <span className="w-32 text-zinc-600">{s.name}</span>
                  <div className="flex-1 bg-zinc-100 rounded-full h-5 overflow-hidden relative">
                    <div className="bg-emerald-500 h-full rounded-full transition-all" style={{ width: `${w}%` }} />
                  </div>
                  <span className="w-12 text-right font-mono">{s.count}</span>
                  {drop !== null && <span className={`w-12 text-right text-[10px] ${drop > 0.5 ? "text-rose-600" : "text-zinc-400"}`}>-{fmtPct(drop)}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Operatori */}
      <div className="border border-zinc-200 rounded-xl p-4" data-testid="dash-operatori">
        <h3 className="font-semibold mb-3">Top Operatori</h3>
        {ops.length === 0 ? <p className="text-zinc-400 italic text-xs">Nessun operatore con lead</p> : (
          <table className="w-full text-xs">
            <thead className="text-zinc-500"><tr><th className="text-left py-1">Operatore</th><th>Lead</th><th>Convertiti</th><th>Conv.%</th><th className="text-right">Premio gestito</th></tr></thead>
            <tbody>
              {ops.slice(0, 10).map((o) => (
                <tr key={o.operatore_id} className="border-t border-zinc-100">
                  <td className="py-1 truncate max-w-[200px]">{o.operatore_id}</td>
                  <td className="text-center">{o.lead_gestiti}</td>
                  <td className="text-center text-emerald-700">{o.convertiti}</td>
                  <td className="text-center">{fmtPct(o.conversion_rate)}</td>
                  <td className="text-right font-mono">{fmtEur(o.premio_gestito_eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Distribuzione compagnie */}
      <div className="border border-zinc-200 rounded-xl p-4" data-testid="dash-compagnie">
        <h3 className="font-semibold mb-3">Distribuzione Compagnie</h3>
        {compagnie.length === 0 ? <p className="text-zinc-400 italic text-xs">Nessuna compagnia attiva</p> : (
          <div className="space-y-1">
            {compagnie.slice(0, 8).map((c) => {
              const max = Math.max(...compagnie.map(x => x.premio_totale_eur), 1);
              const w = (c.premio_totale_eur / max) * 100;
              return (
                <div key={c.compagnia} className="flex items-center gap-2 text-xs">
                  <span className="w-32 truncate">{c.compagnia}</span>
                  <div className="flex-1 bg-zinc-100 rounded h-4 overflow-hidden">
                    <div className="bg-sky-500 h-full" style={{ width: `${w}%` }} />
                  </div>
                  <span className="w-16 text-right text-zinc-500">{c.polizze_attive} pol.</span>
                  <span className="w-24 text-right font-mono">{fmtEur(c.premio_totale_eur)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, title, value, sub, color = "zinc", testid }) {
  const colorMap = {
    zinc: "text-zinc-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  return (
    <div className="border border-zinc-200 rounded-xl p-3" data-testid={testid}>
      <Icon className={`w-4 h-4 ${colorMap[color]}`} />
      <p className="text-xs text-zinc-500 mt-1">{title}</p>
      <p className={`text-xl font-bold ${colorMap[color]}`}>{value}</p>
      <p className="text-[10px] text-zinc-400 truncate">{sub}</p>
    </div>
  );
}
